<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { sendPasswordResetEmail } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getAuthErrorMessage, routeForBackendAuthState, useAuthStore } from '@/stores/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { isOfficeAddinHost } from '@/office/utils';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaGoogleButton from '@/components/design/domain/auth/GaiaGoogleButton.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { error, loading } = storeToRefs(authStore);

// Set when arriving via the verification mail's continue URL without a live
// Firebase session (the router guard forwards those landings here).
const showVerifiedNudge = computed(() => 'verified' in route.query);

// Set when the session guard signed this tab out because the account lost
// access (see sessionGuardStore).
const revokedMessage = computed((): string | null => {
  switch (route.query.reason) {
    case 'deactivated':
      return 'Your account has been deactivated. Contact your organisation admin to regain access.';
    case 'deleted':
      return 'Your account has been deleted.';
    case 'revoked':
      return 'Your access has been revoked. Contact your organisation admin.';
    default:
      return null;
  }
});

const email = ref<string>('');
const password = ref<string>('');
const isGoogleRedirecting = ref(false);

// Maintenance message from environment variable
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
const maintenanceMessage = computed(() => import.meta.env.VITE_MAINTENANCE_MESSAGE || '');
const maintenanceDismissed = ref(false);

// Forgot password state
const showForgotPasswordModal = ref<boolean>(false);
const resetEmail = ref<string>('');
const resetPasswordLoading = ref<boolean>(false);
const resetPasswordError = ref<string | null>(null);
const resetPasswordSuccess = ref<boolean>(false);

onMounted((): void => {
  if (isOfficeAddinHost()) {
    void router.replace('/office-login');
    return;
  }

  if (authStore.isAuthenticated) {
    void router.push('/');
  }
});

// Partial-auth states (unverified / unprovisioned) sign in without throwing;
// route them to the page that resolves their state instead of home.
function postLoginRoute(): string {
  return routeForBackendAuthState(authStore.backendAuthState, isOfficeAddinHost() ? '/vault' : '/');
}

async function handleSubmit(): Promise<void> {
  try {
    await authStore.signIn(email.value, password.value);
    await router.push(postLoginRoute());
  } catch (error_: unknown) {
    logger.error('Login error:', error_);
  }
}

async function handleGoogleLogin(): Promise<void> {
  try {
    isGoogleRedirecting.value = isOfficeAddinHost();
    await authStore.signInWithGoogle();
    await router.push(postLoginRoute());
  } catch (error_: unknown) {
    logger.error('Google login error:', error_);
  } finally {
    isGoogleRedirecting.value = false;
  }
}

function closeResetModal(): void {
  showForgotPasswordModal.value = false;
}

/** The reset modal opens pre-filled with whatever is already in the email field. */
function openResetModal(): void {
  resetEmail.value = email.value;
  resetPasswordError.value = null;
  resetPasswordSuccess.value = false;
  showForgotPasswordModal.value = true;
}

async function handleResetPassword(): Promise<void> {
  if (!resetEmail.value) {
    resetPasswordError.value = 'Please enter your email address';
    return;
  }

  resetPasswordLoading.value = true;
  resetPasswordError.value = null;

  try {
    await sendPasswordResetEmail(getFirebaseAuth(), resetEmail.value);
    // The dialog switches to its confirmation state and waits for "Done" —
    // a timed auto-close would yank the confirmation away mid-read.
    resetPasswordSuccess.value = true;
  } catch (error_: unknown) {
    if (error_ instanceof FirebaseError) {
      resetPasswordError.value = getAuthErrorMessage(error_.code);
      logger.error('Password reset error:', error_.code, error_.message);
    } else {
      resetPasswordError.value = 'An unexpected error occurred';
      logger.error('Password reset error:', error_);
    }
  } finally {
    resetPasswordLoading.value = false;
  }
}
</script>

<template>
  <!-- The split-screen shell (brand panel + card column) is AuthLayout's; this
       page is only the card. -->
  <gaia-auth-card title="Welcome back" subtitle="Sign in to your workspace.">
    <n-flex vertical :size="16">
      <gaia-banner
        v-if="maintenanceMessage && !maintenanceDismissed"
        plain
        type="warning"
        closable
        @close="maintenanceDismissed = true"
      >
        {{ maintenanceMessage }}
      </gaia-banner>
      <gaia-banner v-if="showVerifiedNudge" plain type="success">
        Email verified. Log in to continue.
      </gaia-banner>
      <gaia-banner v-if="revokedMessage" plain type="error">{{ revokedMessage }}</gaia-banner>
      <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>

      <gaia-form-field label="Email" variant="uppercase">
        <gaia-input
          :value="email"
          size="large"
          placeholder="you@firm.com"
          :disabled="loading"
          :input-props="{ type: 'email', autocomplete: 'email' }"
          style="width: 100%"
          @update:value="(v: string) => (email = v.toLowerCase())"
          @keydown.enter="handleSubmit"
        />
      </gaia-form-field>

      <gaia-form-field label="Password" variant="uppercase">
        <template #action>
          <gaia-link @click="openResetModal">Forgot password?</gaia-link>
        </template>
        <gaia-password-input
          v-model:value="password"
          size="large"
          placeholder="••••••••"
          :disabled="loading"
          :input-props="{ autocomplete: 'current-password' }"
          style="width: 100%"
          @keydown.enter="handleSubmit"
        />
      </gaia-form-field>
    </n-flex>

    <gaia-button
      type="primary"
      size="large"
      block
      :loading="loading"
      :style="{ marginTop: '22px' }"
      @click="handleSubmit"
    >
      Log in
    </gaia-button>

    <gaia-divider variant="subtle">or</gaia-divider>
    <gaia-google-button :disabled="loading" @click="handleGoogleLogin">
      {{ isGoogleRedirecting ? 'Redirecting to Google…' : 'Continue with Google' }}
    </gaia-google-button>

    <template #footer>
      By logging in you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>

  <n-flex align="center" justify="center" :size="6" :style="{ marginTop: '18px' }">
    <gaia-text size="dense" tone="tertiary">New to Gaia Insights?</gaia-text>
    <gaia-link to="/sign-up">Create an account</gaia-link>
  </n-flex>

  <!-- Forgot password. Two states in one dialog, exactly as the mock draws it:
       the request form, then the confirmation. No header bar — the serif title
       lives in the body, so there is no close ✕ competing with Cancel. -->
  <gaia-modal
    v-model:show="showForgotPasswordModal"
    width="380px"
    content-style="padding: 26px 26px 22px"
    :closable="false"
  >
    <gaia-auth-status v-if="resetPasswordSuccess" compact tone="success" title="Check your inbox">
      <template #icon>
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      We sent a reset link to <strong>{{ resetEmail }}</strong
      >.
      <template #actions>
        <gaia-button type="primary" size="large" block @click="closeResetModal">Done</gaia-button>
      </template>
    </gaia-auth-status>

    <template v-else>
      <!-- Explicit margin: n-h* carries naive's Typography headerMargin. -->
      <gaia-header :level="2" variant="display" size="dialog" :style="{ margin: '0 0 6px' }">
        Reset password
      </gaia-header>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginBottom: '18px', lineHeight: 1.6 }"
      >
        Enter your email and we'll send you a reset link.
      </gaia-text>

      <gaia-form-field label="Email" variant="uppercase">
        <gaia-input
          v-model:value="resetEmail"
          size="large"
          placeholder="you@firm.com"
          :input-props="{ type: 'email', autocomplete: 'email' }"
          style="width: 100%"
          @keydown.enter="handleResetPassword"
        />
      </gaia-form-field>

      <gaia-banner v-if="resetPasswordError" plain type="error" :style="{ marginTop: '14px' }">
        {{ resetPasswordError }}
      </gaia-banner>

      <!-- Equal halves, not a right-aligned pair: neither is the obvious
           default here, so the mock gives them the same weight. -->
      <n-flex :size="10" :wrap="false" :style="{ marginTop: '20px' }">
        <gaia-button size="large" :style="{ flex: 1 }" @click="closeResetModal">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="large"
          :style="{ flex: 1 }"
          :loading="resetPasswordLoading"
          @click="handleResetPassword"
        >
          Send reset link
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
